// Theme: light | dark. Persisted in localStorage; toggled by setting
// data-theme on <html>. Tailwind dark variants pick this up via the config:
//   darkMode: ['class', '[data-theme="dark"]']
//
// Initial value resolution order:
//   1. localStorage('fuelio.admin.theme')
//   2. system prefers-color-scheme
//   3. fallback 'dark' (the original admin look)

import { useEffect, useState, useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark'

const KEY = 'fuelio.admin.theme'

function readInitial(): Theme {
  if (typeof window === 'undefined') return 'dark'
  const saved = localStorage.getItem(KEY)
  if (saved === 'light' || saved === 'dark') return saved
  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light'
  return 'dark'
}

function applyTheme(t: Theme) {
  document.documentElement.dataset.theme = t
  // Strip the legacy `class="dark"` if present so it doesn't fight the attribute.
  document.documentElement.classList.remove('dark')
}

// Set the attribute *before* React mounts so the first paint matches the
// saved preference (avoids a light-flash on dark systems).
export function bootstrapTheme() {
  applyTheme(readInitial())
}

const listeners = new Set<() => void>()
function notify() { listeners.forEach((fn) => fn()) }

export function useTheme(): [Theme, (next: Theme) => void] {
  const theme = useSyncExternalStore<Theme>(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => ((document.documentElement.dataset.theme as Theme | undefined) ?? 'dark'),
    () => 'dark',
  )

  function setTheme(next: Theme) {
    applyTheme(next)
    localStorage.setItem(KEY, next)
    notify()
  }

  // Track system changes only when the user hasn't explicitly chosen.
  useEffect(() => {
    if (localStorage.getItem(KEY)) return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const handler = (e: MediaQueryListEvent) => {
      applyTheme(e.matches ? 'light' : 'dark')
      notify()
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return [theme, setTheme]
}
